import React, { useEffect, useRef, useState } from 'react';
import { Prompt } from 'react-router-dom';
import Select from 'react-select';
import { Alert, Button, Card, CardBody, Col, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Spinner, Table } from 'reactstrap';
import { useMachineModelConfigurationCopy } from '../../../hooks/analytes/useMachineModelConfigurationCopy';
import { ConfigurationCopyIssues as Issues } from './ConfigurationCopyIssues';
import './MachineModelConfigurationCopy.css';

const entityNames = {
    AppCode: 'Códigos de aplicación',
    AppCodeMethod: 'Métodos por código',
    ExamMethod: 'Métodos de examen',
    ExamMethodMachineModel: 'Asociaciones con el modelo',
    AnalyteAgeGenderRange: 'Rangos por edad y género',
    AnalyteNumberAlert: 'Alertas numéricas',
    AnalyteTextAlert: 'Alertas de texto',
    AnalyteNumberCustomValue: 'Valores personalizados',
    AppCodeTextEquivalence: 'Equivalencias de texto'
};

const modelName = model => model ? `${model.brand || 'Sin marca'} · ${model.model}` : '';
const totalRecords = counts => Object.values(counts || {}).reduce((sum, count) => sum + count, 0);

const Counts = ({ counts }) => (
    <dl className="configuration-copy__counts">
        {Object.entries(entityNames).map(([entity, label]) => (
            <div key={entity}><dt>{label}</dt><dd>{(counts[entity] || 0).toLocaleString('es-MX')}</dd></div>
        ))}
    </dl>
);

const Direction = ({ preview }) => (
    <div className="configuration-copy__direction">
        <div><small>Origen</small><strong>{modelName(preview.source)}</strong></div>
        <i className="fa fa-long-arrow-right" aria-hidden="true" />
        <div><small>Destino</small><strong>{modelName(preview.target)}</strong></div>
    </div>
);

const downloadReport = (result, preview) => {
    const blob = new Blob([JSON.stringify({ source: preview.source, target: preview.target, ...result }, null, 2)], { type: 'application/json;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `copia-configuracion-${result.source_machine_model_id}-${result.target_machine_model_id}.json`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
};

const ConfigurationCopyWorkflow = ({ onCopyCompleted, onBusyChange }) => {
    const copy = useMachineModelConfigurationCopy(onCopyCompleted);
    const {
        models, loadingModels, modelError, loadModels, sourceId, targetId, selectModel,
        codeEdits, editCode, resetCodes, emptyCodes, preview, previewCurrent, result, error, phase,
        canPreview, canCopy, generatePreview, confirming, setConfirming, executeCopy, reset
    } = copy;
    const busy = phase !== 'idle';
    const copying = phase === 'copy';
    const resultHeading = useRef(null);
    const options = models.map(model => ({ value: model.id_machine_model, label: `${modelName(model)} (#${model.id_machine_model})` }));

    useEffect(() => {
        if (onBusyChange) onBusyChange(copying);
        const preventUnload = event => {
            event.preventDefault();
            event.returnValue = '';
        };
        if (copying) window.addEventListener('beforeunload', preventUnload);
        return () => {
            window.removeEventListener('beforeunload', preventUnload);
            if (onBusyChange) onBusyChange(false);
        };
    }, [copying, onBusyChange]);

    useEffect(() => {
        if (result && resultHeading.current) resultHeading.current.focus();
    }, [result]);

    return <>
        <Prompt when={copying} message="La copia está en curso. Si sales, podrías perder la confirmación del resultado. ¿Deseas salir?" />
        <ol className="configuration-copy__steps" aria-label="Etapas de la copia">
            {['Seleccionar modelos', 'Revisar configuración', 'Resultado'].map((step, index) => (
                <li key={step} className={(result ? 2 : preview ? 1 : 0) === index ? 'is-current' : ''}
                    aria-current={(result ? 2 : preview ? 1 : 0) === index ? 'step' : undefined}>
                    <span>{index + 1}</span>{step}
                </li>
            ))}
        </ol>

        {result ? <section aria-labelledby="configuration-copy-result">
            <Alert color="success" role="status">
                <h4 id="configuration-copy-result" ref={resultHeading} tabIndex={-1}><i className="fa fa-check-circle" aria-hidden="true" /> Copia completada</h4>
                <p className="mb-0">Se crearon {totalRecords(result.counts).toLocaleString('es-MX')} registros para el destino. La configuración original y el histórico conservaron sus registros y referencias.</p>
            </Alert>
            <Direction preview={preview} />
            <Counts counts={result.counts} />
            <p>Ya puedes editar los rangos y las alertas de la copia. Los catálogos compartidos, como métodos y exámenes, siguen teniendo alcance global.</p>
            <Issues issues={result.warnings} />
            <details className="configuration-copy__details">
                <summary>Ver correspondencias de registros creados</summary>
                {Object.entries(result.id_mappings).map(([entity, mappings]) => <details key={entity} className="configuration-copy__mapping">
                    <summary>{entityNames[entity] || entity} ({mappings.length})</summary>
                    <div className="configuration-copy__table-scroll">
                        <Table size="sm" striped>
                            <caption className="sr-only">Correspondencias de {entityNames[entity] || entity}</caption>
                            <thead><tr><th scope="col">ID original</th><th scope="col">ID nuevo</th></tr></thead>
                            <tbody>{mappings.map((mapping, index) => <tr key={`${mapping.target_id}-${index}`}>
                                <td>{mapping.source_id == null ? 'Asociación nueva' : mapping.source_id}</td><td>{mapping.target_id}</td>
                            </tr>)}</tbody>
                        </Table>
                    </div>
                </details>)}
            </details>
            <div className="configuration-copy__actions">
                <Button color="primary" outline onClick={() => downloadReport(result, preview)}><i className="fa fa-download" aria-hidden="true" /> Descargar reporte</Button>
                <Button color="primary" onClick={reset}>Preparar otra copia</Button>
            </div>
        </section> : <>
            <section aria-labelledby="configuration-copy-models">
                <h5 id="configuration-copy-models">Selecciona el origen y el destino</h5>
                <p className="text-muted">Ambos modelos deben estar registrados en Marcas. Solo se muestran modelos activos.</p>
                {modelError && <Alert color="danger" role="alert">{modelError} <Button color="link" onClick={loadModels} disabled={loadingModels}>Reintentar carga</Button></Alert>}
                {!loadingModels && !modelError && models.length < 2 && <Alert color="warning">Se necesitan al menos dos modelos activos. Regístralos en Marcas y <Button color="link" onClick={loadModels}>actualiza la lista</Button>.</Alert>}
                <Row>
                    {[
                        { type: 'source', label: 'Modelo de origen', value: sourceId, opposite: targetId, help: 'Se tomarán sus métodos, rangos y valores vigentes.' },
                        { type: 'target', label: 'Modelo de destino', value: targetId, opposite: sourceId, help: 'Recibirá una configuración adicional e independiente.' }
                    ].map(field => <Col md="6" key={field.type}>
                        <FormGroup>
                            <Label for={`copy-${field.type}`}>{field.label}</Label>
                            <Select inputId={`copy-${field.type}`} classNamePrefix="select" options={options}
                                value={options.find(option => option.value === Number(field.value)) || null}
                                onChange={option => selectModel(field.type, option ? option.value : '')}
                                isOptionDisabled={option => option.value === Number(field.opposite)}
                                isDisabled={busy || loadingModels || !!modelError} isLoading={loadingModels} isClearable
                                placeholder={loadingModels ? 'Cargando modelos…' : 'Buscar marca o modelo…'}
                                noOptionsMessage={() => 'No hay modelos disponibles'} loadingMessage={() => 'Cargando…'}
                                aria-describedby={`copy-${field.type}-help`} />
                            <small id={`copy-${field.type}-help`} className="form-text text-muted">{field.help}</small>
                        </FormGroup>
                    </Col>)}
                </Row>
                <div className="configuration-copy__scope">
                    <i className="fa fa-info-circle" aria-hidden="true" />
                    <div>La copia crea registros nuevos y mantiene los métodos predeterminados actuales. Los catálogos de métodos, exámenes y analitos seguirán compartidos.
                        <details><summary>Ver alcance</summary>
                            <p className="mb-0">Se incluyen códigos de aplicación, métodos asociados, rangos por edad y género, alertas, valores personalizados y equivalencias de texto vigentes. Quedan fuera los registros inactivos o eliminados, las asociaciones manuales sin AppCode, las exclusiones de interfaz, las máquinas físicas y sus sucursales. No se cambia la máquina utilizada para procesar resultados.</p>
                        </details>
                    </div>
                </div>
                <div className="configuration-copy__actions">
                    <span className="text-muted">La vista previa no guarda cambios.</span>
                    <Button color="primary" outline={!!preview} disabled={!canPreview} onClick={generatePreview}>
                        {phase === 'preview' && <Spinner size="sm" className="mr-2" />}
                        {phase === 'preview' ? 'Revisando configuración…' : preview ? 'Actualizar vista previa' : 'Generar vista previa'}
                    </Button>
                </div>
            </section>

            {error && <Alert color="danger" role="alert" className="mt-3">{error}</Alert>}
            {copying && <Alert color="info" role="status" className="mt-3"><Spinner size="sm" className="mr-2" /> Creando la configuración del destino. Mantén esta pestaña abierta hasta recibir el resultado.</Alert>}

            {preview && <section className="configuration-copy__review" aria-labelledby="configuration-copy-review" aria-busy={busy}>
                <div className="configuration-copy__review-heading">
                    <h5 id="configuration-copy-review">Vista previa de la copia</h5>
                    <span className={`configuration-copy__badge ${!previewCurrent ? 'is-pending' : preview.can_execute ? 'is-ready' : 'is-blocked'}`}>
                        {!previewCurrent ? 'Requiere nueva revisión' : preview.can_execute ? 'Lista para copiar' : 'Copia bloqueada'}
                    </span>
                </div>
                {!previewCurrent && !busy && <Alert color="warning" role="status">Esta vista previa ya no habilita la copia. Actualízala para validar los datos y los códigos.</Alert>}
                <Direction preview={preview} />
                <h6>Registros a crear: {totalRecords(preview.counts).toLocaleString('es-MX')}</h6>
                <Counts counts={preview.counts} />
                <Issues issues={preview.conflicts} conflict />
                <Issues issues={preview.warnings} />

                <div className="configuration-copy__review-heading">
                    <h6 className="mb-0">Códigos de aplicación ({preview.app_codes.length})</h6>
                    <Button color="link" size="sm" disabled={busy || !Object.keys(codeEdits).length} onClick={resetCodes}>Restablecer códigos originales</Button>
                </div>
                <p className="text-muted">Se conservan los códigos del origen. Puedes ajustar los códigos del destino antes de copiar; cada ajuste requiere actualizar la vista previa.</p>
                <div className="configuration-copy__table-scroll">
                    <Table striped size="sm" className="configuration-copy__codes">
                        <caption className="sr-only">Códigos de aplicación incluidos en la copia</caption>
                        <thead><tr><th scope="col">Analito</th><th scope="col">Exámenes</th><th scope="col">App Code Actual</th><th scope="col">Nuevo App Code</th></tr></thead>
                        <tbody>{preview.app_codes.map(code => {
                            const value = codeEdits[code.source_app_code_id] !== undefined ? codeEdits[code.source_app_code_id] : code.target_code;
                            return <tr key={code.source_app_code_id}>
                                <td>{code.analyte_name || 'Analito no disponible'}</td>
                                <td>{code.exams && code.exams.length > 0 ? <ul className="mb-0 pl-3">
                                    {code.exams.map(exam => <li key={exam.id_exam}>{exam.name}</li>)}
                                </ul> : <span className="text-muted">Sin exámenes asociados</span>}</td>
                                <td>{code.source_code}</td>
                                <td><Input aria-label={`Nuevo App Code para ${code.analyte_name || 'Analito no disponible'} (${code.source_code})`} value={value}
                                    onChange={event => editCode(code.source_app_code_id, event.target.value)} disabled={busy}
                                    invalid={!value.trim()} autoComplete="off" />
                                    {!value.trim() && <small className="text-danger">El código es obligatorio.</small>}
                                </td>
                            </tr>;
                        })}</tbody>
                    </Table>
                </div>
                {emptyCodes && <p className="text-danger" role="alert">Completa todos los códigos antes de actualizar la vista previa.</p>}
                <div className="configuration-copy__actions">
                    <span className="text-muted">Revisa las advertencias y los códigos antes de continuar.</span>
                    <Button color="primary" disabled={!canCopy} onClick={() => setConfirming(true)}><i className="fa fa-copy" aria-hidden="true" /> Continuar con la copia</Button>
                </div>
            </section>}
        </>}

        <Modal className="configuration-copy-modal" isOpen={confirming} toggle={() => setConfirming(false)} centered labelledBy="configuration-copy-confirm-title">
            <ModalHeader id="configuration-copy-confirm-title" toggle={() => setConfirming(false)} closeAriaLabel="Cerrar">Confirmar copia de configuración</ModalHeader>
            <ModalBody>
                {preview && <>
                    <p>Se copiará la configuración vigente de <strong>{modelName(preview.source)}</strong> a <strong>{modelName(preview.target)}</strong>.</p>
                    <p>Se crearán <strong>{totalRecords(preview.counts).toLocaleString('es-MX')} registros nuevos</strong>. Los registros existentes y el histórico se conservarán.</p>
                    {preview.warnings.length > 0 && <Alert color="warning">La vista previa contiene {preview.warnings.length} advertencia(s). Confirma solo después de revisarlas.</Alert>}
                    <p className="mb-0">El servidor volverá a validar la configuración antes de guardar.</p>
                </>}
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" outline onClick={() => setConfirming(false)}>Volver a revisar</Button>
                <Button color="primary" disabled={!canCopy} onClick={executeCopy}>Confirmar y crear copia</Button>
            </ModalFooter>
        </Modal>
    </>;
};

export const MachineModelConfigurationCopy = ({ onCancel, onCopyCompleted, onBusyChange }) => {
    const [accepted, setAccepted] = useState(false);
    return <Card className="configuration-copy">
        <CardBody>
            <header className="configuration-copy__header">
                <span className="configuration-copy__icon"><i className={`fa ${accepted ? 'fa-copy' : 'fa-shield'}`} aria-hidden="true" /></span>
                <div><h4>Copiar configuración entre modelos</h4><p>Crea una configuración adicional con los métodos y valores de referencia de otro modelo.</p></div>
            </header>
            {!accepted ? <section className="configuration-copy__gate" aria-labelledby="configuration-copy-access">
                <Alert color="warning" role="alert">
                    <h5 id="configuration-copy-access"><i className="fa fa-exclamation-triangle" aria-hidden="true" /> Función reservada para usuarios autorizados</h5>
                    <p>Esta herramienta copia métodos, valores de referencia y alertas que se utilizan en la configuración del laboratorio. Úsala únicamente si cuentas con autorización para realizar este proceso.</p>
                    <p className="mb-0">Antes de guardar podrás revisar el origen, el destino y todos los registros incluidos. La copia conservará la configuración original y el histórico.</p>
                </Alert>
                <p className="text-muted">Al continuar confirmas que estás autorizado y que revisarás la configuración antes de ejecutar la copia. Los permisos se validan con tu sesión.</p>
                <div className="configuration-copy__actions">
                    <Button color="secondary" outline onClick={onCancel}>Volver a vinculación de analitos</Button>
                    <Button color="primary" onClick={() => setAccepted(true)}>Entiendo y estoy autorizado</Button>
                </div>
            </section> : <ConfigurationCopyWorkflow onCopyCompleted={onCopyCompleted} onBusyChange={onBusyChange} />}
        </CardBody>
    </Card>;
};
